import { createFileRoute } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import protocolMd from "@/content/lead-protocol.md?raw";

export const Route = createFileRoute("/agent/protocol")({
  head: () => ({
    meta: [
      { title: "Lead Protocol — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ProtocolPage,
});

function ProtocolPage() {
  const download = () => {
    const blob = new Blob([protocolMd], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "currency-thoughts-lead-protocol.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Lead Protocol" nav={AGENT_NAV}>
        <div className="max-w-3xl mx-auto">
          <Eyebrow>Standard Operating Procedure</Eyebrow>
          <h2
            className="mt-3 font-display font-semibold text-navy-800"
            style={{ fontSize: "40px", letterSpacing: "-0.01em" }}
          >
            The <em className="text-bronze-600 italic font-normal">Lead Protocol</em>
          </h2>
          <p className="mt-2 font-sans text-[14px] text-charcoal-500 max-w-xl">
            The working contract every Advisor follows from first contact to fully
            onboarded family. Read it. Live it. Improve it.
          </p>

          <div className="mt-6">
            <SecondaryButton onClick={download}>Download a copy</SecondaryButton>
          </div>

          <div className="mt-10 bg-cream-50 border border-bronze-600/20 rounded-[4px] p-8 shadow-soft">
            <article className="protocol-prose font-sans text-[15px] text-charcoal-900 leading-[1.7]">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{protocolMd}</ReactMarkdown>
            </article>
          </div>
        </div>

        <style>{`
          .protocol-prose h1 { display: none; }
          .protocol-prose h2 {
            font-family: var(--font-display, "Playfair Display", serif);
            font-weight: 600; color: #002D68;
            font-size: 26px; margin-top: 2.2em; margin-bottom: 0.6em;
            border-bottom: 1px solid rgba(168,139,92,0.4); padding-bottom: 0.3em;
          }
          .protocol-prose h3 {
            font-family: var(--font-display, "Playfair Display", serif);
            font-weight: 600; color: #0A3369;
            font-size: 19px; margin-top: 1.6em; margin-bottom: 0.4em;
          }
          .protocol-prose p { margin: 0.9em 0; }
          .protocol-prose ul, .protocol-prose ol { margin: 0.8em 0 0.8em 1.2em; }
          .protocol-prose li { margin: 0.3em 0; }
          .protocol-prose strong { color: #0A3369; font-weight: 600; }
          .protocol-prose em { color: #6B7280; }
          .protocol-prose hr {
            border: 0; border-top: 1px solid rgba(168,139,92,0.3);
            margin: 2em 0;
          }
          .protocol-prose table {
            width: 100%; border-collapse: collapse; margin: 1.2em 0;
            font-size: 13px;
          }
          .protocol-prose th {
            text-align: left; background: #002D68; color: #F5F1E8;
            padding: 10px 12px; font-weight: 600;
            font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em;
          }
          .protocol-prose td {
            padding: 10px 12px; border-bottom: 1px solid rgba(168,139,92,0.25);
            vertical-align: top;
          }
          .protocol-prose tr:last-child td { border-bottom: 0; }
        `}</style>
      </PortalShell>
    </ProtectedRoute>
  );
}